import { useFormikContext } from 'formik';
import { get, isFinite, keyBy } from 'lodash/fp';
import { type ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type FinanceProductFormValues } from '..';
import EditableTable, { type Columns } from './EditableTable';

type Record = { term: number; value: number } & { key: string };

type TermValueTableProps = {
    settingsKey: string;
    valueTitle: ReactNode;
    disabled: boolean;
};

const TermValueTable = ({ settingsKey, valueTitle, disabled }: TermValueTableProps) => {
    const { values, setFieldValue } = useFormikContext<FinanceProductFormValues>();
    const type = get(`${settingsKey}.type`, values);
    const table = get(`${settingsKey}.table`, values);

    const records = useMemo<Partial<Record>[]>(() => {
        const min = values?.term?.min;
        const max = values?.term?.max;
        const step = values?.term?.step;
        if (type !== 'table' || !isFinite(min) || !isFinite(max) || !isFinite(step)) {
            return [];
        }

        const recordByTerm = keyBy(({ term }) => term, table);

        const results = [];
        for (let term = min; term <= max; term += step) {
            results.push({ term, value: recordByTerm[term]?.value, key: term.toString() });
        }

        return results;
    }, [type, values?.term?.min, values?.term?.max, values?.term?.step, table]);

    const onUpdate = useCallback(
        (record: Record) => {
            const { term: target } = record;
            const index = records.findIndex(({ term }) => term === target);

            // clone current records
            const current = [...records];
            // update target
            current.splice(index, 1, record);

            setFieldValue(
                `${settingsKey}.table`,
                current.filter(({ value }) => isFinite(value)).map(({ term, value }) => ({ term, value }))
            );
        },
        [records, setFieldValue, settingsKey]
    );

    const { t } = useTranslation(['financeProductDetails']);

    const columns = useMemo<Columns<Record>>(
        () => [
            { title: t('financeProductDetails:tables.term.title'), dataIndex: 'term', fixed: true },
            {
                title: valueTitle,
                dataIndex: 'value',
                onCell: record => ({ record, dataIndex: 'value', isEditable: !disabled, onUpdate }),
            },
        ],
        [onUpdate, valueTitle, t, disabled]
    );

    return <EditableTable columns={columns} records={records} />;
};

export default TermValueTable;
